import { useFinancialsStore } from './financials-store';
import type {
  AllocationOfCostsMeasures,
  Charts,
  ChartType,
  CostVersusOutputMeasures,
  View,
} from './financials-store.type';

/**
 * Sets the view in the Financials store.
 *
 * @param {View} view - The view to set
 * @return {void}
 */
function setView(view: View): void {
  useFinancialsStore.setState({ view }, false, '[Financials Store] setView');
}

/**
 * Sets the view by in the Financials store.
 *
 * @param {ViewBy} viewBy - The view by to set
 * @return {void}
 */
function setViewBy(viewBy: 'points' | 'tasks'): void {
  useFinancialsStore.setState({ viewBy }, false, '[Financials Store] setViewBy');
}

/**
 * Sets the selected chart in the Financials store.
 *
 * @param {Charts} chart - The chart to set
 * @return {void}
 */
function setChart(chart: Charts): void {
  useFinancialsStore.setState({ chart }, false, '[Financials Store] setChart');
}

/**
 * Sets the selected start date in the Financials store.
 *
 * @param {Date | null} startDate - The selected start date
 * @return {void}
 */
function setStartDate(startDate: Date | null): void {
  useFinancialsStore.setState({ startDate }, false, '[Financials Store] setStartDate');
}

/**
 * Sets the selected end date in the Financials store.
 *
 * @param {Date | null} endDate - The selected end date
 * @return {void}
 */
function setEndDate(endDate: Date | null): void {
  useFinancialsStore.setState({ endDate }, false, '[Financials Store] setEndDate');
}

/**
 * Sets the selected team in the Financials store.
 *
 * @param {string | null} team - The selected team
 * @return {void}
 */
function setTeam(team: string | null): void {
  useFinancialsStore.setState({ team }, false, '[Financials Store] setTeam');
}

/**
 * Sets the selected board in the Financials store.
 *
 * @param {string | null} board - The selected board
 * @return {void}
 */
function setBoard(board: string | null): void {
  useFinancialsStore.setState({ board }, false, '[Financials Store] setBoard');
}

/**
 * Sets the selected allocation of costs measure in the Financials store.
 *
 * @param {AllocationOfCostsMeasures} measure - The measure to set
 * @return {void}
 */
function setAllocationOfCostsMeasure(measure: AllocationOfCostsMeasures): void {
  useFinancialsStore.setState((state) => ({
    filters: { ...state.filters, allocationOfCosts: { ...state.filters.allocationOfCosts, selectedMeasure: measure } },
  }));
}

/**
 * Sets the selected allocation of costs trend lines in the Financials store.
 *
 * @param {string[]} trendLines - The trend lines to set
 * @return {void}
 */
function setAllocationOfCostsTrendLines(trendLines: string[]): void {
  useFinancialsStore.setState((state) => ({
    filters: {
      ...state.filters,
      allocationOfCosts: { ...state.filters.allocationOfCosts, selectedTrendLines: trendLines },
    },
  }));
}
/**
 * Sets the selected allocation of costs options in the Financials store.
 *
 * @param {string[]} options - The options to set
 * @return {void}
 */
function setAllocationOfCostsOptions(options: string[]): void {
  useFinancialsStore.setState((state) => ({
    filters: {
      ...state.filters,
      allocationOfCosts: { ...state.filters.allocationOfCosts, selectedOptions: options },
    },
  }));
}

/**
 * Sets the selected initiative IDs in the Financials store.
 *
 * @param {string[]} initiativeIds - The initiative IDs to set
 * @return {void}
 */
function setCostByInitiativeInitiativeIds(initiativeIds: string[]): void {
  useFinancialsStore.setState((state) => ({
    filters: {
      ...state.filters,
      costByInitiative: { ...state.filters.costByInitiative, selectedInitiativeIds: initiativeIds },
    },
  }));
}

/**
 * Sets the selected cost by initiative trend lines in the Financials store.
 *
 * @param {string[]} trendLines - The trend lines to set
 * @return {void}
 */
function setCostByInitiativeTrendLines(trendLines: string[]): void {
  useFinancialsStore.setState((state) => ({
    filters: {
      ...state.filters,
      costByInitiative: { ...state.filters.costByInitiative, selectedTrendLines: trendLines },
    },
  }));
}

/**
 * Sets the selected allocation of costs chart type in the Financials store.
 *
 * @param {ChartType} chartType - The chart type to set
 * @return {void}
 */
function setAllocationOfCostsChartType(chartType: ChartType): void {
  useFinancialsStore.setState((state) => ({
    filters: {
      ...state.filters,
      allocationOfCosts: { ...state.filters.allocationOfCosts, selectedChartType: chartType },
    },
  }));
}

/**
 * Sets the selected chart type in the Financials store.
 *
 * @param {ChartType} chartType - The chart type to set
 * @return {void}
 */
function setCostByInitiativeChartType(chartType: ChartType): void {
  useFinancialsStore.setState((state) => ({
    filters: {
      ...state.filters,
      costByInitiative: { ...state.filters.costByInitiative, selectedChartType: chartType },
    },
  }));
}

/**
 * Sets the cost versus output measures in the Financials store.
 *
 * @param {CostVersusOutputMeasures[]} measures - The measures to set
 * @return {void}
 */
function setCostVersusOutputMeasures(measures: CostVersusOutputMeasures[]): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        costVersusOutput: {
          ...state.filters.costVersusOutput,
          selectedMeasures: measures,
        },
      },
    }),
    false,
    '[Financials Store] setCostVersusOutputFilters',
  );
}

/**
 * Sets the selected chart type in the Financials store.
 *
 * @param {ChartType} chartType - The chart type to set
 * @return {void}
 */
function setCostVersusOutputChartType(chartType: ChartType): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        costVersusOutput: {
          ...state.filters.costVersusOutput,
          selectedChartType: chartType,
        },
      },
    }),
    false,
    '[Financials Store] setCostVersusOutputChartType',
  );
}

/**
 * Sets whether to show trend lines in the cost versus output chart.
 *
 * @param {boolean} showTrendLines - Whether to show trend lines
 * @return {void}
 */
function setCostVersusOutputTrendLines(trendLines: CostVersusOutputMeasures[]): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        costVersusOutput: {
          ...state.filters.costVersusOutput,
          selectedTrendLines: trendLines,
        },
      },
    }),
    false,
    '[Financials Store] setCostVersusOutputTrendLines',
  );
}

/**
 * Sets the selected year in the Financials store.
 *
 * @param {number | null} year - The selected year
 * @return {void}
 */
function setTableSelectedYear(year: number | null): void {
  useFinancialsStore.setState(
    (state) => ({ table: { selectedYear: year, searchTerm: state.table.searchTerm } }),
    false,
    '[Financials Store] setTableSelectedYear',
  );
}

/**
 * Sets the search term in the Financials store.
 *
 * @param {string | null} searchTerm - The search term to set
 * @return {void}
 */
function setTableSearchTerm(searchTerm: string | null): void {
  useFinancialsStore.setState(
    (state) => ({ table: { selectedYear: state.table.selectedYear, searchTerm: searchTerm ?? undefined } }),
    false,
    '[Financials Store] setTableSearchTerm',
  );
}

/**
 * Resets all financials filters to their default state.
 * This is useful when changing portfolios to ensure filters are relevant to the new context.
 *
 * @return {void}
 */
function resetAllFinancialsFilters(): void {
  useFinancialsStore.setState(
    (state) => ({
      filters: {
        ...state.filters,
        costVersusOutput: {
          ...state.filters.costVersusOutput,
          selectedTrendLines: [],
        },
        allocationOfCosts: {
          ...state.filters.allocationOfCosts,
          selectedOptions: [],
          selectedTrendLines: [],
        },
        costByInitiative: {
          ...state.filters.costByInitiative,
          selectedInitiativeIds: [],
          selectedTrendLines: [],
        },
      },
    }),
    false,
    '[Financials Store] resetAllFinancialsFilters',
  );
}

export {
  resetAllFinancialsFilters,
  setAllocationOfCostsChartType,
  setAllocationOfCostsMeasure,
  setAllocationOfCostsOptions,
  setAllocationOfCostsTrendLines,
  setBoard,
  setChart,
  setCostByInitiativeChartType,
  setCostByInitiativeInitiativeIds,
  setCostByInitiativeTrendLines,
  setCostVersusOutputChartType,
  setCostVersusOutputMeasures,
  setCostVersusOutputTrendLines,
  setEndDate,
  setStartDate,
  setTableSearchTerm,
  setTableSelectedYear,
  setTeam,
  setView,
  setViewBy,
};
